import PropTypes from "prop-types";
import React, { CSSProperties, MouseEvent, MouseEventHandler, PureComponent } from "react";
import { ContentState, Editor, EditorProps, EditorState, SelectionState } from "draft-js";
import getFragmentFromSelection from "draft-js/lib/getFragmentFromSelection";
import Label from "./label";
import theme from "styles/theme";
import { $, merge } from "glamor";
import classNames from "classnames";
import _ from "lodash";
import shallowequal from "shallowequal";
//import EditIcon from 'components/icons/edit';

const styles = {
    base: {
        margin: "0 0 16px",
        position: "relative",
    },
    content: {
        fontSize: "16px",
        lineHeight: 1.5,
        color: theme.palette.darkerGrey,
        whiteSpace: "normal",
    },
    contentEditable: {
        backgroundColor: theme.palette.editing,
    },
    contentEditing: {
        backgroundColor: theme.palette.editing,
    },
    contentForm: {
        border: `solid 1px ${theme.palette.lightGrey}`,
        borderRadius: "4px",
        padding: "8px 12px",
    },
    counter: {
        position: "absolute",
        top: "0px",
        right: "10px",
        color: theme.palette.grey,
    },
    maxLimit: {
        color: theme.palette.warning,
    },
};
const overrides = merge(
    $(" .public-DraftEditorPlaceholder-inner", {
        whiteSpace: "normal",
    })
);

interface TextInlineProps {
    /**
     * Initializes the value in the internal component state while not mounted.
     * All changes after the component is mounted are neglected (uncontrolled mode).
     */
    defaultValue?: string;

    /**
     * Overrides the internal value state (controlled mode).
     * Don't use this prop to set a value from the server-driven model state, because
     * the update API roundtrip delay might cause new keystrokes in the component being
     * overwritten with the stale model state.
     */
    value?: string;
    editable?: boolean;
    label?: string;
    placeholder?: string;
    onChange(value: string): void;
    style?: object;
    editorStyle?: object;
    labelStyle?: object;
    form?: boolean;
    showEditIcon?: boolean;
    defaultFocus?: boolean;
    updateTimeout?: number;
    maxLength?: number;
    multiLine?: boolean;
    showCharacterCount?: boolean;
    customStyleFn?: EditorProps["customStyleFn"];
    dataTest?: string;
    inputId?: string;
}

interface TextInlineState {
    editorState: EditorState;
    focus: boolean;
    hover: boolean;
    value: string;
}

/** @augments {Component<any, any>} */
class TextInline extends PureComponent<TextInlineProps, TextInlineState> {
    static propTypes = {
        defaultValue: PropTypes.string,
        value: PropTypes.string,
        editable: PropTypes.bool,
        label: PropTypes.string,
        placeholder: PropTypes.string,
        onChange: PropTypes.func.isRequired,
        style: PropTypes.object,
        editorStyle: PropTypes.object,
        labelStyle: PropTypes.object,
        form: PropTypes.bool,
        showEditIcon: PropTypes.bool,
        defaultFocus: PropTypes.bool,
        updateTimeout: PropTypes.number,
        maxLength: PropTypes.number,
        multiLine: PropTypes.bool,
        showCharacterCount: PropTypes.bool,
        customStyleFn: PropTypes.func,
        dataTest: PropTypes.string,
        inputId: PropTypes.string,
    };

    static defaultProps = {
        customStyleFn: () => {},
        defaultValue: "",
        editable: false,
        form: false,
        defaultFocus: false,
        multiLine: true,
        updateTimeout: 1000,
        showCharacterCount: false,
        dataTest: "",
    };

    readonly handleFocus: MouseEventHandler;
    readonly handleBlur: MouseEventHandler;
    private readonly handleMouseEnter: MouseEventHandler;
    private readonly handleMouseLeave: MouseEventHandler;
    private _editor: Editor;
    private mounted = false;

    // TODO: re-initialize this method to handle unlikely but still viable changes of updateTimeout and onChange props (when re-coded with hooks)
    private onChangeDebounced = _.debounce(
        distinctUntilChanged((value: string) => {
            this.props.onChange?.(value);
        }),
        this.props.updateTimeout
    );

    constructor(props) {
        super(props);
        const value = this.props.defaultValue;
        this.state = {
            focus: false,
            hover: false,
            value,
            editorState: EditorState.createWithContent(ContentState.createFromText(value)),
        };

        this.handleMouseEnter = this.handleInteraction.bind(this, "hover", true);
        this.handleMouseLeave = this.handleInteraction.bind(this, "hover", false);
        this.handleFocus = this.handleInteraction.bind(this, "focus", true);
        this.handleBlur = this.handleInteraction.bind(this, "focus", false);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { defaultValue, value } = nextProps;
        const { value: prevValue, editorState } = this.state;
        if (_.isString(value) && value !== prevValue.trim()) {
            this.setValue(value, editorState);
        } else if (!this.mounted && _.isString(defaultValue) && defaultValue !== prevValue.trim()) {
            this.setValue(defaultValue, editorState);
        }
    }

    componentDidMount() {
        if (this.props.defaultFocus) {
            this.handleStartEditing();
        }
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.defaultFocus && !prevProps.defaultFocus) {
            this.handleStartEditing();
        }
    }

    private handleBeforeInput = (text, editorState) => {
        const contentState = editorState.getCurrentContent();
        const currentText = contentState.getPlainText();
        const selectedText = getSelectionText(editorState);
        const totalLength = currentText.length - selectedText.length + text.length;
        return totalLength > this.props.maxLength ? "handled" : "not-handled";
    };

    private handlePastedText = (text, html, editorState) => {
        return this.handleBeforeInput(text, editorState);
    };

    private handleChange = (editorState) => {
        const { maxLength, editable } = this.props;
        const value = editorState.getCurrentContent().getPlainText();

        if (editable) {
            this.onChangeDebounced.cancel();
        }

        if (maxLength && value && value.length > maxLength && value.length >= this.state.value.length) {
            this.setValue(value, editorState);
            return;
        }

        //Note: Useful to have your onChange fn check if the value is actually different to prevent
        //useless patch calls
        this.setState({ editorState, value });
        if (editable) {
            this.onChangeDebounced(value);
        }
    };

    private setValue(value: string, currentState: EditorState) {
        const { maxLength } = this.props;
        value = value.substring(0, maxLength);

        // easy way to force refresh the EditorState even if content has not changed
        const newEditorState = EditorState.createWithContent(ContentState.createFromText(value));

        const currentSelection = currentState.getSelection();
        const focusKey = newEditorState.getSelection().getAnchorKey();

        //Clone the old selection state except for the content key, which has changed b/c of new content
        const selectionState = SelectionState.createEmpty(focusKey);
        const focusOffset = Math.min(currentSelection.getFocusOffset(), value.length, maxLength);
        const anchorOffset = Math.min(currentSelection.getAnchorOffset(), value.length, maxLength);
        const hasFocus = currentSelection.getHasFocus();
        const isBackward = currentSelection.getIsBackward();
        const updatedSelection = selectionState.merge({
            focusKey,
            focusOffset,
            anchorOffset,
            hasFocus,
            isBackward,
        });
        const editorState = EditorState.forceSelection(newEditorState, updatedSelection);

        this.setState({
            editorState,
            value,
        });
    }

    private handleInteraction(event: "hover" | "focus", isEnter: boolean) {
        const newState = {};
        newState[event] = isEnter;
        this.setState(newState);
    }

    private handleStartEditing = (e?: MouseEvent) => {
        const { editable } = this.props;

        if (this._editor) {
            this._editor.focus();
        }
        if (editable && e) {
            e.stopPropagation();
        }
    };

    render() {
        const {
            editable,
            label,
            placeholder,
            style,
            editorStyle,
            labelStyle,
            form,
            showEditIcon,
            multiLine,
            maxLength,
            showCharacterCount,
            dataTest,
            inputId,
            customStyleFn,
        } = this.props;
        const { editorState, focus, hover, value } = this.state;
        const reachedLimit = value.length >= maxLength;

        return (
            <div
                style={Object.assign({} as CSSProperties, styles.base, style)}
                onClick={this.handleStartEditing}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
            >
                {label && (
                    <Label editable={editable} showEditIcon={showEditIcon} labelStyle={labelStyle}>
                        {label}
                    </Label>
                )}

                {maxLength && value && editable && showCharacterCount && (
                    <span style={Object.assign({} as CSSProperties, styles.counter, reachedLimit && styles.maxLimit)}>
                        {value.length}/{maxLength}
                    </span>
                )}

                <div
                    data-test={dataTest}
                    key={inputId || dataTest}
                    style={Object.assign(
                        {} as CSSProperties,
                        styles.content,
                        form && styles.contentForm,
                        editorStyle,
                        editable && hover && !form && styles.contentEditable,
                        editable && !form && focus && styles.contentEditing
                    )}
                    {...overrides}
                    className={classNames({
                        "text-editor": true,
                        active: focus || hover,
                    })}
                >
                    <Editor
                        data-test={dataTest}
                        editorKey={inputId || dataTest}
                        key={inputId || dataTest}
                        ref={(ref) => (this._editor = ref)}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        stripPastedStyles={true}
                        readOnly={!editable}
                        editorState={editorState}
                        handleBeforeInput={this.handleBeforeInput}
                        handlePastedText={this.handlePastedText}
                        onChange={this.handleChange}
                        placeholder={placeholder}
                        handleReturn={() => (multiLine ? "not-handled" : "handled")}
                        handleDrop={() => "handled"}
                        webDriverTestID={dataTest}
                        customStyleFn={customStyleFn}
                    />
                </div>
            </div>
        );
    }
}

const getSelectionText = (editorState: EditorState, delimiter = "") => {
    const fragment = getFragmentFromSelection(editorState);
    return fragment?.map((block) => block.getText()).join(delimiter) ?? "";
};

/**
 * Returns a new function with the same signature as `fn` which calls the input fn only with non-repeating parameters.
 * In case of repeating parameters, the previous invocation result is returned.
 *
 * @param fn - Function to wrap.
 */
const distinctUntilChanged = <F extends (...args: any[]) => any>(fn: F) => {
    let prevArgs: Parameters<F>;
    let prevResult: ReturnType<F>;
    let isCalled = false;

    function wrapper(this: any, ...args: Parameters<F>) {
        if (!isCalled || !shallowequal(prevArgs, args)) {
            prevResult = fn.apply(this, args);
            isCalled = true;
            prevArgs = args;
        }
        return prevResult;
    }

    return wrapper as F;
};

export default TextInline;
